/**
 * /onboarding/roles — Built-in Role Selection
 *
 * Shown immediately after signup, before billing plan selection.
 * The new OrgAdmin selects which system built-in roles should be available
 * within their organization.  OrgAdmin is always enabled and shown as locked.
 *
 * After saving, the user is routed to /billing/select-plan.
 */

import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Shield, Users, Headphones, ClipboardList, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { AUTH_ME_KEY } from "@/lib/auth";

type BuiltinRole = {
  id: string;
  name: string;
  description: string | null;
  isOrgAdmin: boolean;
  enabled: boolean;
  selectable: boolean;
};

type RolesResponse = {
  roles: BuiltinRole[];
  isLegacyOrg: boolean;
};

// Human-friendly metadata for well-known built-in roles.
const ROLE_META: Record<string, { label: string; description: string; icon: typeof Shield }> = {
  org_admin:  { label: "Org Admin",   description: "Full organization admin access — always enabled.",       icon: Shield },
  member:     { label: "Member",      description: "Standard access for everyday users.",                     icon: Users },
  supervisor: { label: "Supervisor",  description: "Supervise agents, review calls and live sessions.",       icon: Users },
  live_agent: { label: "Live Agent",  description: "Handle live-chat conversations and support queues.",      icon: Headphones },
  auditor:    { label: "Auditor",     description: "Read-only access to audit logs and compliance reports.",  icon: ClipboardList },
};

function roleMeta(name: string | null) {
  return ROLE_META[name ?? ""] ?? {
    label: name ?? "Unknown",
    description: "System role.",
    icon: Shield,
  };
}

export default function OnboardingRolesPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // Fetch current built-in role catalog + enabled state.
  const { data, isLoading, isError } = useQuery<RolesResponse>({
    queryKey: ["/api/organization/builtin-roles"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/organization/builtin-roles");
      return res.json();
    },
  });

  // Local selection state — initialised from server response.
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [initialised, setInitialised] = useState(false);

  if (data && !initialised) {
    const init: Record<string, boolean> = {};
    for (const r of data.roles) init[r.id] = r.enabled;
    setSelected(init);
    setInitialised(true);
  }

  const saveMutation = useMutation({
    mutationFn: async (roleIds: string[]) => {
      const res = await apiRequest("POST", "/api/organization/builtin-roles", {
        roleIds,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message ?? "Failed to save role availability.");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast({ title: "Roles saved", description: "Your organization's available roles have been set." });
      // 1. Optimistically clear the flag so any synchronous render that fires
      //    between now and the network round-trip can't bounce the user back.
      queryClient.setQueryData(AUTH_ME_KEY, (old: any) => {
        if (!old?.organization) return old;
        return { ...old, organization: { ...old.organization, needsRolesOnboarding: false } };
      });
      // 2. Await a server refetch so the App route guard is reading confirmed
      //    server data (needsRolesOnboarding:false) before navigate() fires.
      //    Without this, React may render a stale value, the guard's catch-all
      //    <Redirect to="/onboarding/roles"> intercepts the navigation, and the
      //    user stays on this page — the bug reported as "first click doesn't work".
      try {
        await queryClient.refetchQueries({ queryKey: [...AUTH_ME_KEY] });
      } catch {
        // Network error — the optimistic update is in cache; navigate anyway.
      }
      navigate("/onboarding/compliance", { replace: true });
    },
    onError: (err: Error) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
  });

  function toggle(roleId: string, isOrgAdmin: boolean) {
    if (isOrgAdmin) return; // OrgAdmin is always enabled
    setSelected(prev => ({ ...prev, [roleId]: !prev[roleId] }));
  }

  function handleContinue() {
    if (!data) return;
    const enabledIds = data.roles
      .filter(r => r.isOrgAdmin || selected[r.id])
      .map(r => r.id);
    saveMutation.mutate(enabledIds);
  }

  function handleSkip() {
    // Skipping saves all selectable roles as enabled — ensures no accidental lockout.
    if (!data) { navigate("/onboarding/compliance", { replace: true }); return; }
    const allIds = data.roles.map(r => r.id);
    saveMutation.mutate(allIds);
  }

  const selectableRoles = data?.roles.filter(r => r.selectable) ?? [];
  const lockedRoles     = data?.roles.filter(r => !r.selectable) ?? [];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Set up your organization roles</h1>
          <p className="text-muted-foreground text-sm">
            Choose which built-in roles should be available to your organization.
            You can adjust this later from the Roles settings page.
          </p>
        </div>

        {/* Always-enabled (locked) roles */}
        {(isLoading || lockedRoles.length > 0) && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Always enabled</CardTitle>
              <CardDescription>These roles are required and cannot be disabled.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {isLoading
                ? Array.from({ length: 1 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)
                : lockedRoles.map(role => {
                    const meta = roleMeta(role.name);
                    const Icon = meta.icon;
                    return (
                      <div key={role.id} className="flex items-start gap-3 py-1 opacity-75">
                        <Checkbox id={`role-${role.id}`} checked disabled className="mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <Label htmlFor={`role-${role.id}`} className="font-medium flex items-center gap-2 cursor-default">
                            <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                            {meta.label}
                            <Badge variant="outline" className="text-[10px] h-4">Required</Badge>
                          </Label>
                          <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
                        </div>
                      </div>
                    );
                  })
              }
            </CardContent>
          </Card>
        )}

        {/* Selectable built-in roles */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Available built-in roles</CardTitle>
            <CardDescription>
              Select the roles you want to use in your organization. Only enabled roles
              appear in user assignment dropdowns.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading
              ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
              : isError
              ? <p className="text-sm text-destructive">Failed to load roles. Please refresh.</p>
              : selectableRoles.map(role => {
                  const meta = roleMeta(role.name);
                  const Icon = meta.icon;
                  const checked = selected[role.id] ?? false;
                  return (
                    // No onClick on the outer container. Only the checkbox and
                    // its <Label> (via htmlFor) respond to clicks. Clicking the
                    // description text or any empty card area must not toggle.
                    <div key={role.id} className="flex items-start gap-3 py-1">
                      <Checkbox
                        id={`role-${role.id}`}
                        checked={checked}
                        onCheckedChange={() => toggle(role.id, role.isOrgAdmin)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        {/* Label covers only the name row; htmlFor wires it to
                            the checkbox so click/keyboard Space both work. */}
                        <Label htmlFor={`role-${role.id}`} className="font-medium flex items-center gap-2 cursor-pointer">
                          <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                          {meta.label}
                        </Label>
                        {/* Plain <p> — not inside the label — so clicking the
                            description text has no checkbox side-effect. */}
                        <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
                      </div>
                    </div>
                  );
                })
            }
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleContinue}
            disabled={isLoading || saveMutation.isPending}
            className="flex-1"
          >
            {saveMutation.isPending ? "Saving…" : "Continue"}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            disabled={saveMutation.isPending}
            className="text-muted-foreground"
          >
            Enable all &amp; skip
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          You can create custom roles for your organization from the Roles page at any time.
        </p>
      </div>
    </div>
  );
}
